/*
 * <products-scroller> — the only JS in Products pro.
 * Native horizontal scroll (swipe / trackpad) needs no JS. This wires the optional
 * prev/next arrows and, when a data-url is present (Recommendations source), lazily
 * fetches related products via Shopify's Product Recommendations API and fills the
 * track once the row scrolls into view. No dependencies.
 */
if (!customElements.get('products-scroller')) {
  customElements.define(
    'products-scroller',
    class ProductsScroller extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('.plist__scroller');
        this.track = this.querySelector('.plist__track');
        this.initArrows();

        // Recommendations source: fetch when near the viewport if the track is empty.
        if (this.dataset.url && this.track && this.track.children.length === 0) {
          new IntersectionObserver(
            (entries, observer) => {
              if (!entries[0].isIntersecting) return;
              observer.disconnect();
              this.loadRecommendations();
            },
            { rootMargin: '0px 0px 400px 0px' }
          ).observe(this);
        }
      }

      initArrows() {
        this.arrows = this.querySelector('.plist__arrows');
        this.prev = this.querySelector('.plist__arrow--prev');
        this.next = this.querySelector('.plist__arrow--next');
        if (!this.scroller) return;
        this.prev?.addEventListener('click', () => this.scrollByCards(-1));
        this.next?.addEventListener('click', () => this.scrollByCards(1));
        this.scroller.addEventListener('scroll', () => this.update(), { passive: true });
        window.addEventListener('resize', () => this.update(), { passive: true });
        this.update();
      }

      step() {
        const card = this.track?.querySelector('.plist__card');
        const gap = this.track ? parseFloat(getComputedStyle(this.track).columnGap) || 0 : 0;
        return card ? card.getBoundingClientRect().width + gap : this.scroller.clientWidth * 0.8;
      }

      scrollByCards(dir) {
        this.scroller.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
      }

      update() {
        if (!this.prev || !this.next || !this.scroller) return;
        const max = this.scroller.scrollWidth - this.scroller.clientWidth - 1;
        this.classList.toggle('plist--fits', max <= 0);
        if (!this.classList.contains('plist--recommendations-pending') && this.arrows) {
          this.arrows.hidden = max <= 0;
        }
        this.prev.disabled = this.scroller.scrollLeft <= 0;
        this.next.disabled = this.scroller.scrollLeft >= max;
      }

      loadRecommendations() {
        const url = `${this.dataset.url}&section_id=${this.dataset.sectionId}&product_id=${this.dataset.productId}`;
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const fetched = new DOMParser().parseFromString(text, 'text/html').querySelector('.plist__track');
            if (!fetched || fetched.children.length === 0) {
              this.closest('.shopify-section')?.setAttribute('hidden', '');
              return;
            }
            this.track.innerHTML = fetched.innerHTML;
            this.classList.remove('plist--recommendations-pending');
            this.removeAttribute('aria-busy');
            this.update();
          })
          .catch(() => this.closest('.shopify-section')?.setAttribute('hidden', ''));
      }
    }
  );
}
